import ICRank from "@/components/Icon/ICNavbar/ICRank";
import ICVault from "@/components/Icon/ICNavbar/ICVault.tsx";
import { ICWallet } from "@/components/Icon/ICProfile/ICWallet";
import { shortenAddress } from "@/utils/convert/format";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Logout from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import Settings from "@mui/icons-material/Settings";
import { Divider } from "@mui/material";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useLocation, useRouter } from "@tanstack/react-router";
import * as React from "react";
import { useTranslation } from "react-i18next";
import LoginButton from "../../LoginButton/LoginButton";
import { useAuth } from "@/provider/AuthProvider";
import { useNavigationUtils } from "@/utils/navigationHooks";

const MOBILE_MENU_ITEMS = [
  {
    key: "navbar.dashboard",
    path: "/dashboard",
    icon: <ICVault width="24" height="24" />,
    private: false,
  },
  {
    key: "navbar.ranks",
    path: "/ranking",
    icon: <ICRank width="24" height="24" />,
    private: false,
  },
  {
    key: "navbar.menu.profile",
    path: "/user-profile",
    icon: <AccountCircleIcon width="24" height="24" />,
    private: true, // This ensures the item only appears when authenticated
  },
  {
    key: "navbar.menu.settings",
    path: "",
    icon: <Settings width="24" height="24" />,
    private: true,
  },

  {
    key: "navbar.menu.logout",
    path: "",
    icon: <Logout width="24" height="24" />,
    private: true,
  },
];

export default function MobileNavigation() {
  const { t } = useTranslation();
  const router = useRouter();
  const location = useLocation();
  const { address, isAuthenticated } = useAuth();
  const { handleLogout } = useNavigationUtils();
  const isNotFoundPage = location.pathname === "/not-found";

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (key: string, path: string) => {
    if (key === "navbar.menu.logout") {
      handleLogout();
      handleClose();
      return;
    }

    if (key === "navbar.menu.settings") {
      handleClose();
      return;
    }

    handleClose();
    router.navigate({ to: path });
  };

  return (
    <div className="flex items-center">
      {!isAuthenticated && (
        <LoginButton icon={<ICWallet className="size-4" />} />
      )}
      <Button
        variant="text"
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          color: "white",
          "&:hover": {
            color: "white",
            backgroundColor: "transparent",
          },
          padding: 0,
        }}
      >
        <MenuIcon />
      </Button>
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          marginTop: "12px",
          "& .MuiPaper-root": {
            backgroundColor: "var(--background-secondary)",
            borderRadius: "18px",
            border: "1px solid var(--gray-7)",
            overflow: "hidden",
          },
        }}
      >
        {isAuthenticated && (
          <>
            <div className="px-4 pt-1 pb-3 flex items-center gap-2">
              <ICWallet width="16" height="16" />
              <p className="text-sm">{shortenAddress(address ?? "", 6, 4)}</p>
            </div>
            <Divider />
          </>
        )}
        {MOBILE_MENU_ITEMS.map((item) => {
          if (item.private && !isAuthenticated) return null;
          const disableItem =
            isNotFoundPage && item.key !== "navbar.menu.logout";

          return (
            <MenuItem
              key={item.key}
              onClick={() => handleNavigation(item.key, item.path)}
              disabled={disableItem}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                opacity: disableItem ? 0.5 : 1,
                color:
                  location.pathname === item.path
                    ? "var(--brand-primary-green)"
                    : "white",
              }}
            >
              {item.icon}
              {t(item.key)}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
}
4;
